/**
 * The doctor's approval page (public, no login). Reached from the link in the WhatsApp
 * message. The link works only while that sending of the script is waiting.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Clock, FileText, Loader2, PenLine, RefreshCw } from 'lucide-react';
import { decideScript, errText, fetchScriptPage } from '@/lib/connections';
import Lines from '@/components/connect/Lines';
import RichView from '@/components/ui/RichView';

export default function ApprovePage() {
  const { token = '' } = useParams<{ token: string }>();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['script-page', token], queryFn: () => fetchScriptPage(token), retry: false });
  const [asking, setAsking] = useState(false);
  const [changes, setChanges] = useState('');
  const decide = useMutation({
    mutationFn: (v: { d: 'approved' | 'changes_requested'; f?: string }) => decideScript(token, v.d, v.f),
    onSuccess: (p) => qc.setQueryData(['script-page', token], p),
  });
  const p = q.data;

  return (
    <div className="min-h-screen bg-bg text-muted-strong">
      <div className="max-w-2xl mx-auto px-4 py-6 sm:py-10">
        <div className="text-[11px] tracking-[0.2em] uppercase text-muted mb-6">KAF PRO · script approval</div>

        {q.isLoading && <div className="flex items-center gap-2 text-muted"><Loader2 size={16} className="animate-spin" /> Opening the script...</div>}
        {q.isError && (
          <div className="rounded-2xl border border-slate-800 bg-bg-card p-6" data-testid="approve-invalid">
            <h1 className="text-xl font-medium mb-2">This link does not work</h1>
            <p className="text-sm text-muted">It may be incomplete, or the script was removed. The KAF team can send it again.</p>
          </div>
        )}

        {p && (
          <>
            <header className="mb-5">
              <div className="text-sm text-muted">{p.clinic} · <span className="font-mono">{p.code}</span></div>
              <h1 className="text-2xl font-medium leading-snug mt-1" dir="auto">{p.title}</h1>
              <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted"><Clock size={12} /> Shoot: {p.shoot}</div>
            </header>

            {p.state === 'replaced' && (
              <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-5 mb-5 flex gap-3" data-testid="approve-replaced">
                <RefreshCw size={18} className="text-amber-300 shrink-0 mt-0.5" />
                <div><div className="font-medium">There is a newer version</div>
                  <div className="text-sm text-muted">The team changed this script after sending you this link. Use the newest message they sent you.</div></div>
              </div>
            )}
            {p.state === 'approved' && (
              <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-5 mb-5 flex gap-3" data-testid="approve-done">
                <CheckCircle2 size={20} className="text-emerald-300 shrink-0" />
                <div><div className="font-medium text-emerald-200">Approved. Thank you, Dr {p.doctor.replace(/^Dr\.?\s*/i, '')}.</div>
                  <div className="text-sm text-muted">The team has been told and will plan the shoot with this script.</div></div>
              </div>
            )}
            {p.state === 'changes_requested' && (
              <div className="rounded-2xl border border-sky-500/40 bg-sky-500/10 p-5 mb-5 flex gap-3" data-testid="approve-changes-done">
                <PenLine size={18} className="text-sky-300 shrink-0 mt-0.5" />
                <div><div className="font-medium">Your changes are with the team</div>
                  <div className="text-sm text-muted">They will send you the revised script.</div></div>
              </div>
            )}

            {p.script && (
              <article className="rounded-2xl border border-slate-800 bg-bg-card p-5 sm:p-6 mb-6">
                <div className="flex items-center gap-2 text-xs text-muted mb-3"><FileText size={13} /> The script</div>
                <div data-testid="approve-script">{p.script_html
                  ? <RichView html={p.script_html} className="text-[17px] leading-8" />
                  : <Lines text={p.script} lineClassName="text-[17px] leading-8" />}</div>
              </article>
            )}

            {p.state === 'waiting' && (
              <div className="sticky bottom-0 -mx-4 px-4 pb-4 pt-3 bg-gradient-to-t from-bg via-bg to-bg/0">
                {!asking ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button onClick={() => decide.mutate({ d: 'approved' })} disabled={decide.isPending} data-testid="approve-yes"
                      className="rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-medium py-4 text-base transition disabled:opacity-50">
                      {decide.isPending ? 'Saving...' : 'Approve the script'}
                    </button>
                    <button onClick={() => setAsking(true)} disabled={decide.isPending} data-testid="approve-changes"
                      className="rounded-xl border border-slate-600 hover:border-slate-400 py-4 text-base transition">
                      Ask for changes
                    </button>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-slate-700 bg-bg-card p-4 space-y-3">
                    <label className="block text-sm" htmlFor="changes">What would you like changed?</label>
                    <textarea id="changes" value={changes} onChange={(e) => setChanges(e.target.value)} rows={4} dir="auto" autoFocus maxLength={4000}
                      className="w-full rounded-lg border border-slate-700 bg-bg px-3 py-2 text-base focus:outline-none focus:border-accent-400" data-testid="approve-changes-text" />
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setAsking(false)} className="px-4 py-2 text-sm text-muted">Back</button>
                      <button onClick={() => decide.mutate({ d: 'changes_requested', f: changes.trim() })} disabled={!changes.trim() || decide.isPending}
                        className="rounded-lg bg-accent-600 hover:bg-accent-500 text-white px-4 py-2 text-sm disabled:opacity-50" data-testid="approve-changes-send">
                        Send to the team
                      </button>
                    </div>
                  </div>
                )}
                {decide.isError && <p className="mt-2 text-sm text-red-300">{errText(decide.error, 'That did not save. Try again.')}</p>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
